import { useState, useEffect } from 'react';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import {
  GiftIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  UserGroupIcon,
  ClockIcon,
  SparklesIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  ShareIcon,
  ChartBarIcon,
  TicketIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const REWARD_AMOUNT = 250;

export default function ReferralDashboard() {
  const { selectedCompany, getCurrentCompanyId } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [referralInfo, setReferralInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // State for applying a referral code
  const [inputCode, setInputCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [codeValidation, setCodeValidation] = useState(null);

  // Reload data when selected company changes
  useEffect(() => {
    loadReferralData();
  }, [selectedCompany]);

  const loadReferralData = async () => {
    setLoading(true);
    try {
      const [codeRes, statsRes, historyRes, infoRes] = await Promise.all([
        apiHelpers.getReferralCode(),
        apiHelpers.getReferralStats(),
        apiHelpers.getReferralHistory({ limit: 10 }),
        apiHelpers.getReferralInfo()
      ]);

      setReferralCode(codeRes.data.referralCode);
      setShareUrl(codeRes.data.shareUrl);
      setStats(statsRes.data.stats);
      setHistory(historyRes.data.referrals || []);
      setReferralInfo(infoRes.data);
    } catch (error) {
      console.error('Error loading referral data:', error);
      
      // Check if it's a "no company" error (for Super/Managing Admins without a selected company)
      const errorMessage = error.response?.data?.error || '';
      if (errorMessage.includes('No company associated') || errorMessage.includes('Company ID required')) {
        toast.error('Please select a company to view referral information');
      } else {
        toast.error('Unable to load referral data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard!`);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const shareReferral = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Neoshift BI',
          text: `Sign up for Neoshift BI using my referral code and we both get ${REWARD_AMOUNT} credits!`,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(shareUrl, 'Referral link');
        }
      }
    } else {
      copyToClipboard(shareUrl, 'Referral link');
    }
  };

  // Validate referral code as user types
  const validateCode = async (code) => {
    if (!code || code.length < 6) {
      setCodeValidation(null);
      return;
    }
    
    try {
      const response = await apiHelpers.validateReferralCode(code);
      if (response.data.valid) {
        setCodeValidation({
          valid: true,
          message: response.data.referrer?.name 
            ? `Valid code from ${response.data.referrer.name}` 
            : 'Valid referral code'
        });
      } else {
        setCodeValidation({
          valid: false,
          message: response.data.message || 'Invalid referral code'
        });
      }
    } catch (error) {
      setCodeValidation({
        valid: false,
        message: 'Could not validate code'
      });
    }
  };

  // Debounced validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputCode) {
        validateCode(inputCode);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [inputCode]);

  const handleApplyCode = async () => {
    if (!inputCode || !codeValidation?.valid) return;
    
    setApplyingCode(true);
    try {
      const response = await apiHelpers.applyReferralCode(inputCode);
      if (response.data.success) {
        toast.success('Referral code applied successfully!');
        setInputCode('');
        setCodeValidation(null);
        // Reload data to reflect the change
        loadReferralData();
      } else {
        toast.error(response.data.message || 'Unable to apply referral code');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
      if (errorMessage.includes('No company associated') || errorMessage.includes('Company ID required')) {
        toast.error('Please select a company to apply a referral code');
      } else {
        toast.error(errorMessage || 'Unable to apply referral code');
      }
    } finally {
      setApplyingCode(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Check if no company is available
  const currentCompanyId = getCurrentCompanyId();
  if (!currentCompanyId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <GiftIcon className="w-12 h-12 text-surface-300 dark:text-surface-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            No Company Selected
          </h3>
          <p className="text-surface-600 dark:text-surface-400">
            Please select a company to view referral information
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white flex items-center gap-3">
            <GiftIcon className="w-8 h-8 text-primary-500" />
            Refer-a-Friend
          </h1>
          <p className="text-surface-600 dark:text-surface-400 mt-1">
            Give {REWARD_AMOUNT} credits, Get {REWARD_AMOUNT} credits when your friends make their first payment
          </p>
        </div>
        <button
          onClick={loadReferralData}
          className="p-2 text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
        >
          <ArrowPathIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Referral Code Card */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium opacity-90">Your Referral Code</h2>
            <div className="flex items-center gap-3 mt-2">
              <code className="text-3xl font-mono font-bold tracking-wider">
                {referralCode}
              </code>
              <button
                onClick={() => copyToClipboard(referralCode, 'Referral code')}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                {copied ? (
                  <CheckIcon className="w-5 h-5" />
                ) : (
                  <ClipboardDocumentIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => copyToClipboard(shareUrl, 'Referral link')}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <ClipboardDocumentIcon className="w-5 h-5" />
              Copy Link
            </button>
            <button
              onClick={shareReferral}
              className="flex items-center gap-2 px-4 py-2 bg-white text-primary-600 font-medium rounded-lg hover:bg-white/90 transition-colors"
            >
              <ShareIcon className="w-5 h-5" />
              Share
            </button>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-white/20 text-sm opacity-80">
          Share this code with friends. When they sign up and make their first payment, you both receive {REWARD_AMOUNT} credits!
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <UserGroupIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-surface-500 dark:text-surface-400">Total Referrals</p>
              <p className="text-2xl font-bold text-surface-900 dark:text-white">
                {stats?.totalReferrals || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <ClockIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm text-surface-500 dark:text-surface-400">Pending</p>
              <p className="text-2xl font-bold text-surface-900 dark:text-white">
                {stats?.pendingReferrals || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <CheckIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-surface-500 dark:text-surface-400">Completed</p>
              <p className="text-2xl font-bold text-surface-900 dark:text-white">
                {stats?.completedReferrals || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <SparklesIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-surface-500 dark:text-surface-400">Credits Earned</p>
              <p className="text-2xl font-bold text-surface-900 dark:text-white">
                {(stats?.totalCreditsEarned || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Enter Referral Code (if not referred yet) */}
      {!referralInfo?.wasReferred && (
        <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <TicketIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-surface-900 dark:text-white">
                Have a Referral Code?
              </h3>
              <p className="text-surface-600 dark:text-surface-400 text-sm mt-1">
                Enter a friend's referral code. When you make your first payment, you'll both receive {REWARD_AMOUNT} credits!
              </p>
              
              <div className="mt-4 flex gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={inputCode}
                    onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                    placeholder="Enter referral code (e.g., NEOBI-XXXXXXXX)"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      codeValidation?.valid === true
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : codeValidation?.valid === false
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900'
                    } text-surface-900 dark:text-white placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  />
                  {codeValidation && (
                    <div className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                      codeValidation.valid ? 'text-emerald-500' : 'text-red-500'
                    }`}>
                      {codeValidation.valid ? (
                        <CheckIcon className="w-5 h-5" />
                      ) : (
                        <ExclamationCircleIcon className="w-5 h-5" />
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleApplyCode}
                  disabled={!codeValidation?.valid || applyingCode}
                  className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {applyingCode ? 'Applying...' : 'Apply Code'}
                </button>
              </div>
              
              {codeValidation && (
                <p className={`mt-2 text-sm ${
                  codeValidation.valid ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {codeValidation.message}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Your Referral Status (if referred by someone) */}
      {referralInfo?.wasReferred && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
              <GiftIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                You Were Referred!
              </h3>
              <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                {referralInfo.hasReceivedReward ? (
                  <>You've received your {referralInfo.rewardAmount || REWARD_AMOUNT} credit bonus!</>
                ) : (
                  <>Make your first payment to unlock your {referralInfo.rewardAmount || REWARD_AMOUNT} credit bonus!</>
                )}
              </p>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className={`px-2 py-0.5 rounded-full ${
                  referralInfo.hasReceivedReward 
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
                }`}>
                  {referralInfo.hasReceivedReward ? 'Reward Claimed' : 'Pending Payment'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* How It Works */}
      <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 p-6">
        <h2 className="text-lg font-semibold text-surface-900 dark:text-white mb-4">
          How It Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold text-sm flex-shrink-0">
              1
            </div>
            <div>
              <h3 className="font-medium text-surface-900 dark:text-white">Share Your Code</h3>
              <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                Share your unique referral code or link with friends and colleagues
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold text-sm flex-shrink-0">
              2
            </div>
            <div>
              <h3 className="font-medium text-surface-900 dark:text-white">They Sign Up</h3>
              <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                Your friend creates an account using your referral code
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold text-sm flex-shrink-0">
              3
            </div>
            <div>
              <h3 className="font-medium text-surface-900 dark:text-white">Both Get Rewarded</h3>
              <p className="text-sm text-surface-600 dark:text-surface-400 mt-1">
                When they make their first payment, you both get {REWARD_AMOUNT} credits!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Referral History */}
      <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700">
        <div className="p-5 border-b border-surface-200 dark:border-surface-700">
          <h2 className="text-lg font-semibold text-surface-900 dark:text-white">
            Your Referrals
          </h2>
        </div>
        
        {history.length === 0 ? (
          <div className="p-12 text-center">
            <UserGroupIcon className="w-12 h-12 text-surface-300 dark:text-surface-600 mx-auto mb-3" />
            <p className="text-surface-600 dark:text-surface-400">
              No referrals yet. Share your code to get started!
            </p>
          </div>
        ) : (
          <div className="divide-y divide-surface-200 dark:divide-surface-700">
            {history.map((referral) => (
              <div key={referral._id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    referral.status === 'completed' 
                      ? 'bg-emerald-100 dark:bg-emerald-900/30'
                      : referral.status === 'blocked'
                        ? 'bg-red-100 dark:bg-red-900/30'
                        : 'bg-amber-100 dark:bg-amber-900/30'
                  }`}>
                    {referral.status === 'completed' ? (
                      <CheckIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    ) : referral.status === 'blocked' ? (
                      <span className="w-5 h-5 text-red-600 dark:text-red-400">✕</span>
                    ) : (
                      <ClockIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-surface-900 dark:text-white">
                      {referral.referredCompanyId?.name || 'Company'}
                    </p>
                    <p className="text-sm text-surface-500 dark:text-surface-400">
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    referral.status === 'completed'
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                      : referral.status === 'blocked'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
                  }`}>
                    {referral.status === 'completed' ? 'Completed' : 
                     referral.status === 'blocked' ? 'Blocked' : 'Pending'}
                  </span>
                  {referral.status === 'completed' && (
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                      +{referral.referrerRewardAmount || REWARD_AMOUNT} credits
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Conversion Rate */}
      {stats && stats.totalReferrals > 0 && (
        <div className="bg-surface-50 dark:bg-surface-900/50 rounded-xl p-5 flex items-center gap-4">
          <ChartBarIcon className="w-8 h-8 text-primary-500" />
          <div>
            <p className="text-sm text-surface-600 dark:text-surface-400">Conversion Rate</p>
            <p className="text-2xl font-bold text-surface-900 dark:text-white">
              {stats.conversionRate}%
            </p>
          </div>
          <p className="text-sm text-surface-500 dark:text-surface-400 ml-auto">
            {stats.completedReferrals} of {stats.totalReferrals} referrals converted
          </p>
        </div>
      )}
    </div>
  );
}
